import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

// Routes where back button should NOT show (home pages, login/register, etc.)
const NO_BACK_ROUTES = new Set([
  '/login', '/register', '/forgot-password', '/impressum', '/datenschutz', '/agb',
  '/admin', '/admin/dashboard', '/employee/dashboard', '/superadmin/dashboard',
  '/superadmin/customers', '/email-verification-pending',
  '/admin/subscription',  // Eigene "Zurück"-Navigation vorhanden
]);

/**
 * GlobalMobileBackButton
 * Shows a floating bottom-left back button on mobile (<1024px) for all pages
 * that are not "home" pages. Designed specifically for PWA use where there's
 * no browser chrome back button.
 *
 * iOS Safari: Button wird DIREKT `position: fixed` verankert (nur bottom/left),
 * KEIN inset-dimensionierter Overlay (top:0+bottom:0). Ein Overlay mit gleichzeitig
 * top und bottom wird von iOS-WebKit beim Ein-/Ausblenden der Browserleiste falsch
 * neu dimensioniert → der Button rutscht zur Mitte. Direktes fixed+bottom folgt dem
 * sichtbaren Viewport-Unterrand zuverlässig. (Keine transform-Ancestors vorhanden.)
 */
export const GlobalMobileBackButton = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Don't show on home/auth routes or any superadmin route
  const isNoBackRoute = NO_BACK_ROUTES.has(location.pathname)
    || location.pathname.startsWith('/verify-email')
    || location.pathname.startsWith('/reset-password')
    || location.pathname.startsWith('/set-password')
    || location.pathname.startsWith('/superadmin');

  if (isNoBackRoute) return null;

  return (
    <button
      onClick={() => navigate(-1)}
      className="lg:hidden flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-card border border-border shadow-xl text-foreground hover:bg-muted active:scale-95 transition-all duration-150"
      style={{
        position: 'fixed',
        bottom: 'max(calc(env(safe-area-inset-bottom, 0px) + 16px), 20px)',
        left: '16px',
        zIndex: 9999,
      }}
      aria-label="Zurück"
      data-testid="global-mobile-back-btn"
    >
      <ArrowLeft className="h-4 w-4" />
      <span className="text-xs font-medium">Zurück</span>
    </button>
  );
};
